/*
 * Copyright (c) Nexign, JSC, 1992-2024.
 */
/**
 * Hook for checking table footer buttons disabled status and tooltip msg
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import keyBy from 'lodash/keyBy';
import { useSelector } from 'react-redux';

import { ITableReducer } from '@components/Table/reducers/tableReducer';
import {
    makeSelectorTableBasketCount,
    makeSelectorTableBasketParams,
    makeSelectorTableButtons,
    makeSelectorTableButtonsForCheckOnMode,
    makeSelectorTableButtonsMode,
} from '@components/Table/selectors/tableSelectors';
import {
    ETableButtonsMode,
    mapTableFooterToTableName,
    TableFooterNameType,
    TableNameType,
} from '@components/Table/types/tableTypes';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { ISessionStorageTimers } from '@modules/storage/storageReducer';
import { makeSelectTimers } from '@modules/storage/storageSelectors';
import { ITableButtonsMode } from '@modules/table/types';
import declination from '@utils/declination';
import { stringFormatParams } from '@utils/stringFormat';

import { ITableButtonTypes } from '../types';

const useTableFooterDisabled = (
    tableFooterName: TableFooterNameType,
    buttonsProps?: ITableButtonTypes[],
    buttonsModeProps?: ITableButtonsMode,
    basketCountProps?: number,
    basketParamsProps?: any,
) => {
    /* TODO: remove TableNameType */
    const getTableName = useCallback(
        () => mapTableFooterToTableName[tableFooterName] as TableNameType,
        [tableFooterName],
    );

    const translation: ICommonTranslation = useSelector(makeSelectTranslationCommon());

    const notCheckOnCount = useCallback((id: AccessComponentKeyEnum): boolean =>
        tableFooterName === TableFooterNameType.accountId || tableFooterName === TableFooterNameType.subscriberId
        || ((tableFooterName === TableFooterNameType.bills || tableFooterName === TableFooterNameType.billsAccountId)
           && id === AccessComponentKeyEnum.FooterButtonReconciliation),
        [tableFooterName],
    );

    /* TODO: remove basketParamsSelector, use only basketParams */
    const basketParamsSelector = useSelector(makeSelectorTableBasketParams());
    const basketParams = basketParamsProps || basketParamsSelector;

    /* TODO: remove buttonsFromStore, use only buttons */
    const buttonsFromStore: ITableReducer[TableNameType]['buttons'] = useSelector(
        makeSelectorTableButtons(getTableName()),
    );
    const buttons = buttonsProps || buttonsFromStore;

    /* TODO: remove buttonsModeFromStore, use only buttonsMode */
    const buttonsForCheckOnModeFromStore = useSelector(makeSelectorTableButtonsForCheckOnMode(getTableName()));
    const buttonsModeFromStore = useSelector(makeSelectorTableButtonsMode(getTableName()));
    const buttonsForCheckOnMode = buttonsModeProps?.buttonsForCheckOnMode || buttonsForCheckOnModeFromStore;
    const buttonsMode = buttonsModeProps?.mode || buttonsModeFromStore;

    /* TODO: remove countBasketSelector, use only countBasket */
    const countBasketSelector = useSelector(makeSelectorTableBasketCount(getTableName()));
    const countBasket = isNumber(basketCountProps) ? basketCountProps : countBasketSelector;

    const timers: ISessionStorageTimers[] = useSelector(makeSelectTimers());

    const getTranslation = useCallback(
        () => translation.corporateTableFooter.types[getTableName()],
        [getTableName, translation.corporateTableFooter],
    );

    const getMaxCount = useCallback(
        (id: AccessComponentKeyEnum): number | null => {
            return keyBy(buttons, 'buttonName')?.[id]?.maxCount;
        },
        [buttons],
    );

    const checkOnMode = useCallback(
        (mode: ETableButtonsMode, id: AccessComponentKeyEnum) => {
            switch (mode) {
                case ETableButtonsMode.disableAllExceptInArray:
                    return !!buttonsForCheckOnMode.length && !buttonsForCheckOnMode.includes(id);
                default:
                    return false;
            }
        },
        [buttonsForCheckOnMode],
    );

    const isBtnDisabled = useCallback(
        (id: AccessComponentKeyEnum, minCount = 1): boolean => {
            const timer = !!find(timers, { buttonName: id });
            if (timer) return true;

            const maxCount = getMaxCount(id);
            return (
                (!notCheckOnCount(id) && isNumber(countBasket) && countBasket < minCount) ||
                (!notCheckOnCount(id) && isNumber(countBasket) && isNumber(maxCount) && countBasket > maxCount) ||
                (basketParams?.valid && basketParams.valid[id] === false) ||
                (!notCheckOnCount(id) && !!buttonsMode && checkOnMode(buttonsMode, id))
            );
        },
        [buttonsMode, basketParams, countBasket, checkOnMode, getMaxCount, timers, notCheckOnCount],
    );

    const getFooterButtonTooltipMsg = (id: AccessComponentKeyEnum): string | undefined => {
        const maxCount = getMaxCount(id);
        if (buttonsMode && checkOnMode(buttonsMode, id)) {
            return {
                [ETableButtonsMode.disableAllExceptInArray]: getTranslation()?.disabledButton.mode,
            }[buttonsMode];
        }

        if (basketParams?.valid && basketParams.valid[id] === false) {
            return getTranslation()?.[id]?.basketValid;
        }

        const timer = find(timers, { buttonName: id });
        if (timer?.time) {
            const time = +timer?.time / 1000;
            return stringFormatParams(getTranslation()?.disabledButton.timeout || '', {
                [ELocaleKeys.number]: time,
                [ELocaleKeys.seconds]: declination(time, translation.secondsFrom),
            });
        }

        if (maxCount && maxCount >= 1 && countBasket && countBasket > maxCount) {
            const maxSize = getTranslation()?.[id]?.maxSize || getTranslation()?.disabledButton.maxSize || '';
            return stringFormatParams(maxSize, { maxCount });
        }
    };

    return {
        isBtnDisabled,
        getFooterButtonTooltipMsg,
        getMaxCount,
    };
};

export default useTableFooterDisabled;
